import React, { useState, useEffect, useRef } from 'react';
import { WordCard } from '../WordCard';
import { WordTestPage } from '../WordTestPage';
import { playSuccessSound } from '../../utils/audio';

interface WordPracticeProps {
  words: string[];
  wordBankData: Array<{
    word: string;
    pronunciation?: string;
    translation: string;
    persian_translation?: string;
    persian_translation?: string;
    examples: Array<{ 
      dutch: string; 
      english: string;
      persian?: string;
      persian?: string;
    }>;
  }>;
  showWordCards: boolean;
  showWordQuiz: boolean;
  shouldShowQuiz: boolean;
  onWordCardComplete: () => void;
  onQuizComplete: () => void;
}

export function WordPractice({
  words,
  wordBankData,
  showWordCards,
  showWordQuiz,
  shouldShowQuiz,
  onWordCardComplete,
  onQuizComplete,
}: WordPracticeProps) {
  const [currentPhase, setCurrentPhase] = useState<'cards' | 'quiz' | 'complete'>(
    showWordCards ? 'cards' : showWordQuiz ? 'quiz' : 'complete'
  );
  const [nativeLanguage] = useState(() => localStorage.getItem('nativeLanguage') || 'English');
  
  const [isTransitioning, setIsTransitioning] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    
    if (showWordCards) {
      setCurrentPhase('cards');
      setIsTransitioning(false);
    }
    
    return () => {
      mountedRef.current = false;
    };
  }, [wordBankData, showWordCards]);

  const handleWordCardNext = async () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    
    // Update word bank data first
    if (wordBankData.length <= 1) {
      if (shouldShowQuiz) {
        await new Promise(resolve => setTimeout(resolve, 500));
        if (mountedRef.current) {
        setCurrentPhase('quiz');
        }
      } else {
        if (mountedRef.current) {
        setCurrentPhase('complete');
        }
        onQuizComplete();
      }
    }

    await playSuccessSound();
    onWordCardComplete();
    if (mountedRef.current) {
    setIsTransitioning(false);
    }
  };

  const handleQuizComplete = async () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    
    await playSuccessSound();
    if (mountedRef.current) {
    setCurrentPhase('complete');
    }
    onQuizComplete();
    if (mountedRef.current) {
    setIsTransitioning(false);
    }
  };

  if (currentPhase === 'complete') {
    return null;
  }

  if (currentPhase === 'cards' && wordBankData.length > 0) {
    const currentWord = wordBankData[0];
    return (
      <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        <WordCard
          word={currentWord.word}
          pronunciation={currentWord.pronunciation}
          translation={currentWord.translation}
          persian_translation={currentWord.persian_translation}
          examples={currentWord.examples}
          nativeLanguage={nativeLanguage}
          onNext={handleWordCardNext}
        />
      </div>
    );
  }

  if (currentPhase === 'quiz' && shouldShowQuiz) {
    return (
      <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        <WordTestPage
          words={words}
          nativeLanguage={nativeLanguage}
          trackScore={false}
          onComplete={handleQuizComplete}
        />
      </div>
    );
  }

  return null;
}